import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { aiApi } from '../../services/aiApi';
import { api } from '../../services/api';
import { useAmazonProfile } from '../../contexts/AmazonProfileContext';
import Button from '../ui/Button';

const MCP_PREF_KEY = 'sellerclub-assistant-amazon-mcp';

function formatAssistantText(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, lineIdx) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const children = parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-semibold text-slate-900 dark:text-slate-100">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
    return (
      <p key={lineIdx} className={line.trim() ? 'mb-1.5 last:mb-0' : 'mb-2'}>
        {children}
      </p>
    );
  });
}

function buildWelcome(amazonMcpOn, mcpConnected, pagePath) {
  const pageHint = pagePath && pagePath !== '/dashboard'
    ? ` You're on **${pagePath}** — ask "what can I do here?" for page-specific help.`
    : '';
  if (!amazonMcpOn) {
    return {
      role: 'assistant',
      content:
        `Hi — I'm your **SellerClub ads assistant**. I use synced reports, campaign data, and know which page you're on in the app.${pageHint} Turn **Amazon MCP** on below for live Amazon tools when approved.`,
      suggestions: [
        'What can I do on this page?',
        'What is spend and sales today?',
        'Show optimization recommendations',
        'Any anomalies?',
      ],
    };
  }
  if (mcpConnected) {
    return {
      role: 'assistant',
      content:
        'Hi — **Amazon MCP is on.** I\'ll try Amazon\'s MCP tools first, then fall back to SellerClub synced reports if a tool fails. Turn MCP off anytime to use only your database.',
      suggestions: [
        'What is spend and sales today?',
        'Show optimization recommendations',
        'List MCP tools',
      ],
    };
  }
  return {
    role: 'assistant',
    content:
      'Hi — **Amazon MCP is on** but not connected right now. Answers will use **SellerClub cache** until MCP auth works.',
    suggestions: [
      'What is spend and sales today?',
      'Show optimization recommendations',
    ],
  };
}

function readMcpPref() {
  try {
    const v = localStorage.getItem(MCP_PREF_KEY);
    if (v === 'false') return false;
    if (v === 'true') return true;
  } catch {
    // ignore
  }
  return true;
}

export default function DashboardAssistantChat() {
  const { selectedProfile } = useAmazonProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [amazonMcpOn, setAmazonMcpOn] = useState(readMcpPref);
  const [messages, setMessages] = useState([buildWelcome(readMcpPref(), false, location.pathname)]);
  const [mcpStatus, setMcpStatus] = useState(null);
  const scrollRef = useRef(null);

  const profileId = selectedProfile?.profileId;
  const storeLabel = selectedProfile?.accountInfo?.name
    || selectedProfile?.accountName
    || selectedProfile?.profileId
    || 'No store selected';

  const mcpConnected = Boolean(mcpStatus?.connected);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  useEffect(() => {
    if (!open || !profileId) {
      setMcpStatus(null);
      return;
    }
    api.get('/agentic/mcp/status', { params: { profileId } })
      .then((res) => setMcpStatus(res.data?.data || null))
      .catch(() => setMcpStatus(null));
  }, [open, profileId]);

  const setMcpPref = useCallback((on) => {
    setAmazonMcpOn(on);
    try {
      localStorage.setItem(MCP_PREF_KEY, on ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, []);

  const endSession = useCallback(() => {
    setMessages([buildWelcome(amazonMcpOn, mcpConnected, location.pathname)]);
    setInput('');
  }, [amazonMcpOn, mcpConnected, location.pathname]);

  useEffect(() => {
    if (!open) return;
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0].role !== 'assistant') return prev;
      return [buildWelcome(amazonMcpOn, mcpConnected, location.pathname)];
    });
  }, [location.pathname, open, amazonMcpOn, mcpConnected]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);

    const preferAmazonMcp = amazonMcpOn && mcpStatus?.enabled !== false;

    try {
      const response = await aiApi.dashboardChat({
        profileId,
        message: trimmed,
        preferAmazonMcp,
        pageContext: {
          path: location.pathname,
          search: location.search,
          title: document.title,
        },
      });
      const data = response.data;
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || 'No response.',
          suggestions: data.suggestions,
          actions: data.actions,
          source: data.meta?.source,
          meta: data.meta,
        },
      ]);
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message || 'Request failed';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Could not reach the assistant: ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, profileId, amazonMcpOn, mcpStatus?.enabled, location.pathname, location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const dataSourceLabel = !amazonMcpOn
    ? 'SellerClub data'
    : mcpConnected
      ? 'MCP + cache fallback'
      : 'SellerClub data';

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-[4.75rem] right-4 sm:right-6 z-30 flex items-center gap-2 rounded-full bg-orange-600 px-3 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-stone-950"
          aria-label="Open ads assistant"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Ads assistant</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-[4.75rem] right-4 sm:right-6 z-40 flex w-[min(100vw-2rem,400px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          style={{ height: 'min(560px, calc(100vh - 6rem))' }}
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2 min-w-0">
              <SparklesIcon className="h-5 w-5 text-orange-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Ads assistant</p>
                <p className="text-xs text-slate-500 truncate">
                  {storeLabel}
                  <span className="ml-1 text-slate-400">· {dataSourceLabel}</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
              <span className="font-medium">Amazon MCP</span>
              <button
                type="button"
                role="switch"
                aria-checked={amazonMcpOn}
                onClick={() => setMcpPref(!amazonMcpOn)}
                className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                  amazonMcpOn ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    amazonMcpOn ? 'translate-x-4' : ''
                  }`}
                />
              </button>
              <span className={amazonMcpOn ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}>
                {amazonMcpOn ? 'On' : 'Off'}
              </span>
            </label>
            <button
              type="button"
              onClick={endSession}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              End session
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-orange-600 text-white'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {msg.role === 'user' ? msg.content : formatAssistantText(msg.content)}
                  {msg.source && msg.role === 'assistant' && (
                    <p className="mt-2 text-[10px] uppercase tracking-wide text-slate-500">
                      via {String(msg.source).replace(/_/g, ' ')}
                    </p>
                  )}
                  {msg.actions?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {msg.actions.map((action, i) => (
                        action.href ? (
                          <Link
                            key={i}
                            to={action.href}
                            className="text-xs font-medium text-orange-600 hover:underline dark:text-orange-400"
                            onClick={() => setOpen(false)}
                          >
                            {action.label}
                          </Link>
                        ) : null
                      ))}
                    </div>
                  )}
                  {msg.suggestions?.length > 0 && msg.role === 'assistant' && idx === messages.length - 1 && !loading && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {msg.suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => sendMessage(s)}
                          className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-xs text-slate-500 animate-pulse px-1">Thinking…</div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-slate-200 p-3 dark:border-slate-700">
            {!profileId && (
              <p className="text-xs text-amber-700 mb-2">Select a store to get account-specific answers.</p>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={amazonMcpOn ? 'Ask spend, ACOS, recommendations…' : 'Ask from SellerClub reports…'}
                disabled={loading}
                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
              <Button type="submit" disabled={loading || !input.trim()} className="!px-3" aria-label="Send">
                <PaperAirplaneIcon className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
